<script setup>
import { storeToRefs } from 'pinia'
import { useClientsStore } from '@/stores/clients'
import { RMA_ADAPTER_SUPPORTED_VERSIONS } from '@/constants'
import FormFieldInput from '@/components/FormFieldInput.vue'
import FormFieldError from '@/components/FormFieldError.vue'
import FormFieldSelect from '@/components/FormFieldSelect.vue'
import FormFieldCheckbox from '@/components/FormFieldCheckbox.vue'
import FormFieldNumber from '@/components/FormFieldNumber.vue'

const { isLoading, isSaving, selectedClient } = storeToRefs(useClientsStore())

function clearReturnsManagementInputs() {
  const { fields } = selectedClient.value
  fields.warehouseReceiving.$returnsManagementReceivingProgram.inputValue = undefined
  fields.warehouseReceiving.$returnsManagementAutoCloseRmasAfterDays.inputValue = undefined
}

function clearThirdPartyLogisticsInputs() {
  const { fields } = selectedClient.value
  fields.warehouseReceiving.$thirdPartyLogisticsClientSlug.inputValue = undefined
  fields.warehouseReceiving.$thirdPartyLogisticsReceivingProgram.inputValue = undefined
}

function clearCrossdockInputs() {
  const { fields } = selectedClient.value
  fields.warehouseReceiving.$crossdockReceivingProgram.inputValue = undefined
}
</script>

<template>
  <fieldset>
    <div class="form-group col-2">
      <label class="form-label">Warehouse ID *</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$warehouseId"
        :disabled="isLoading || isSaving"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$warehouseId" />
    </div>
    <div class="form-group col-2">
      <label class="form-label">Client ID *</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$clientId"
        :disabled="isLoading || isSaving"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$clientId" />
    </div>
    <div class="form-group col-2">
      <label class="form-label">RMA Adapter *</label>
      <FormFieldSelect
        class="form-select"
        :fieldModel="selectedClient.fields.warehouseReceiving.$rmaAdapterVersion"
        :disabled="isLoading || isSaving"
      >
        <option :value="null">Select Version</option>
        <option
          v-for="(version, index) in RMA_ADAPTER_SUPPORTED_VERSIONS"
          :key="index"
          :value="version"
        >
          {{ `Version ${version}` }}
        </option>
      </FormFieldSelect>
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$rmaAdapterVersion" />
    </div>
  </fieldset>
  <fieldset class="mt-5">
    <div class="form-check">
      <FormFieldCheckbox 
        class="form-check-input"
        :fieldModel="selectedClient.fields.warehouseReceiving.$returnsManagementEnabled"
        :disabled="isLoading || isSaving"
        @change="clearReturnsManagementInputs"
      />
      <label class="form-check-label">Use Returns Management (RM) (Optional)</label>
    </div>
    <div class="form-group col-2 mt-2">
      <label class="form-label">Receiving Program</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$returnsManagementReceivingProgram"
        :disabled="isLoading || isSaving || !selectedClient.fields.warehouseReceiving.$returnsManagementEnabled.inputValue"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$returnsManagementReceivingProgram" />
    </div>
    <div class="form-group col-2 mt-3">
      <div class="d-flex align-items-center">
        Auto-close RMAs after
        <FormFieldNumber
          class="form-control number-input mx-2"
          placeholder="0"
          :fieldModel="selectedClient.fields.warehouseReceiving.$returnsManagementAutoCloseRmasAfterDays"
          :disabled="isLoading || isSaving || !selectedClient.fields.warehouseReceiving.$returnsManagementEnabled.inputValue"
        />
        days
      </div>
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$returnsManagementAutoCloseRmasAfterDays" />
    </div>
  </fieldset>
  <fieldset class="mt-5">
    <div class="form-check">
      <FormFieldCheckbox 
        class="form-check-input"
        :fieldModel="selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsEnabled"
        :disabled="isLoading || isSaving"
        @change="clearThirdPartyLogisticsInputs"
      />
      <label class="form-check-label">Use 3PL client (Optional)</label>
    </div>
    <div class="form-group col-2 mt-3">
      <label class="form-label">Client Slug</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsClientSlug"
        :disabled="isLoading || isSaving || !selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsEnabled.inputValue"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsClientSlug" />
    </div>
    <div class="form-group col-2 mt-2">
      <label class="form-label">Receiving Program</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsReceivingProgram"
        :disabled="isLoading || isSaving || !selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsEnabled.inputValue"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$thirdPartyLogisticsReceivingProgram" />
    </div>
  </fieldset>
  <fieldset class="mt-5">
    <div class="form-check">
      <FormFieldCheckbox 
        class="form-check-input"
        :fieldModel="selectedClient.fields.warehouseReceiving.$crossdockEnabled"
        :disabled="isLoading || isSaving"
        @change="clearCrossdockInputs"
      />
      <label class="form-check-label">Use Crossdock (Optional)</label>
    </div>
    <div class="form-group col-2 mt-2">
      <label class="form-label">Receiving Program</label>
      <FormFieldInput
        type="text"
        class="form-control"
        :fieldModel="selectedClient.fields.warehouseReceiving.$crossdockReceivingProgram"
        :disabled="isLoading || isSaving || !selectedClient.fields.warehouseReceiving.$crossdockEnabled.inputValue"
      />
      <FormFieldError :fieldModel="selectedClient.fields.warehouseReceiving.$crossdockReceivingProgram" />
    </div>
  </fieldset>
</template>

<style lang="scss" scoped>
.number-input {
  width: 72px;
  text-align: center;
}
</style>
